import Vue from "vue";
import BootstrapVue from "bootstrap-vue";

import AppService from "./core/base/shared/AppService";
import AuthService from "./shared/services/AuthService";
import BaseComponent from "./core/base/BaseComponent";

import {
  L,
  LIcon,
  LMap,
  LTileLayer,
  LPopup,
  LMarker,
  LCircleMarker,
} from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
import Vuelidate from "vuelidate";

import PerfectScrollbar from "vue2-perfect-scrollbar";
import "vue2-perfect-scrollbar/dist/vue2-perfect-scrollbar.css";
import vuescroll from "vuescroll";

import Vuebar from "vuebar";
import DateRangePicker from "vue2-daterange-picker";
import flatPickr from "vue-flatpickr-component";
import VueAWN from "vue-awesome-notifications";

import * as Sentry from "@sentry/browser";
import * as Integrations from "@sentry/integrations";

const userLang = navigator.language || navigator.userLanguage;
window.locale = userLang.substring(0, 2);

import store from "./shared/store/AppStore";

window.Vue = Vue;
window.store = store;

window.addEventListener("storage", function (e) {
  if (e.key === "verified_email") {
    localStorage.removeItem("verified_email");
    store.state.user.verified_email = true;
  }
});

Vue.component("l-map", LMap);
Vue.component("l-tile-layer", LTileLayer);
Vue.component("l-marker", LMarker);
Vue.component("l-cirlce-marker", LCircleMarker);
Vue.component("l-popup", LPopup);
Vue.component("l-icon", LIcon);

Vue.use(DateRangePicker);
Vue.use(flatPickr);
Vue.use(BootstrapVue);
Vue.use(Vuelidate);

Vue.use(PerfectScrollbar, {
  options: {
    wheelPropagation: false,
  },
});
Vue.use(vuescroll);

Vue.use(Vuebar);
Vue.use(VueAWN, { labels: { success: "", alert: "" } });

try {
  const stripeKey = document
    .querySelector('meta[name="STRIPE_KEY"]')
    .getAttribute("content");
  window.stripe = Stripe(stripeKey);
} catch (e) {
  console.log(e);
}

const uid = localStorage.getItem("uid");
if (uid) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "accessed_app",
    userID: uid + "_atto",
  });
}

import router from "./routes";

new Vue({
  el: "#app",
  store,
  router: router.options.router,
  components: {
    BaseComponent,
  },
  async beforeCreate() {
    const search = window.location.search;
    const params = new URLSearchParams(search);
    const accessToken = params.get("token");
    const isXero = params.get("redirectToXero");
    const isQuickBooks = params.get("redirectToQuickBooks");
    const redirectTo = params.get("redirectTo");

    if (accessToken) {
      AuthService.setToken(accessToken);
    }

    let _this = this;
    _this.$store.commit("showLoading");
    _this.$moment.locale(window.locale);
    AppService.getInit()
      .then((data) => {
        _this.$store.commit("loggedIn");
        _this.$store.commit("company", data.company);
        _this.$store.commit("companySettings", data.company_settings);
        _this.$store.commit("user", data.user);
        _this.$store.commit("userSettings", data.user_settings);
        _this.$store.commit("timeOption", data.time_option);
        _this.$store.commit("lastTimesheet", data.last_timesheet);
        _this.$store.commit(
          "members",
          data.members !== null ? data.members : [data.user]
        );
        _this.$store.commit("invites", data.invites);
        _this.$store.commit("onTrial", data.on_trial);
        _this.$store.commit("subscribed", data.subscribed);
        _this.$store.commit("thisWeek", data.this_week);
        _this.$store.commit(
          "isConnectedToQuickbooks",
          data.isConnectedToQuickbooks
        );
        _this.$store.commit("isConnectedToXero", data.isConnectedToXero);
        _this.$store.commit("contentLoaded");
        _this.$store.commit("labels", data.labels);
        _this.$store.commit("shareInviteEnabled", data.share_invite_enabled);
        _this.$store.commit("shareInviteUrl", data.share_invite_url);
        _this.$store.commit("userLicense", data.user_license);
        _this.$store.commit(
          "showCustomPassportLicenseNumber",
          data.custom_license_and_passport
        );
        _this.$store.commit(
          "payrollCheckHQEnabled",
          data.payroll_checkhq_web_enabled
        );
        _this.$store.commit("payrollCheckHQUrl", data.checkhq_payroll_url);
        _this.$store.commit("hideLoading");
        AuthService.setUser(data.user);

        const initialLoader = document.getElementById("initial-loader");
        if (initialLoader !== undefined && initialLoader !== null) {
          initialLoader.remove();
        }

        const zone = _this.$moment().utcOffset() / 60;
        const dst = _this.$moment().isDST();
        if (
          zone !== data.user.timezone_offset ||
          dst !== data.user.timezone_dst
        ) {
          AppService.updateTimezone({
            timezone_offset: zone,
            timezone_dst: dst,
          });
        }

        if (window.kochava !== undefined) {
          window.kochava.identify(
            {
              UserID: data.user.uid,
              Email: data.user.email,
            },
            function (err) {}
          );
        }

        if (!uid && window.dataLayer !== undefined) {
          window.dataLayer.push({
            event: "accessed_app",
            userID: data.user.uid + "_atto",
          });
        }

        if (accessToken) {
          _this.$router.replace({
            path: isXero
              ? `/settings/xero/${isXero}`
              : isQuickBooks
              ? "/settings/quickbooks/import"
              : "/settings",
          });
        }

        // if(window.profitwell !== undefined) {
        //     window.profitwell('start', {'user_email': data.user.email});
        // }
      })
      .catch((error) => {
        console.log(error);
        if (error.response !== undefined && error.response.status !== 401) {
          _this.$store.commit("contentLoaded");
          _this.$store.commit("hideLoading");
        }
      });

    AppService.getInitLists()
      .then((data) => {
        _this.$store.commit("weekdays", data.weekdays);
        _this.$store.commit("payrollSchedulers", data.payroll_schedulers);
        _this.$store.commit("companyJobs", data.company_jobs);
        _this.$store.commit("companyDepartments", data.company_departments);
        _this.$store.commit("companyPto", data.company_pto);
        _this.$store.commit("jobSites", data.job_sites);
        _this.$store.commit("breakCodes", data.break_codes);
        _this.$store.commit("currencies", data.currencies);
        _this.$store.commit("countries", data.countries);
      })
      .catch((error) => {
        console.log(error);
      });
  },
});

/*Sentry.init({
    release: "atto@2.0.2",
    dsn: 'https://c6255a5562044394921beb4b881664a4@sentry.io/1459948',
    integrations: [
        new Integrations.Vue({
            Vue,
            attachProps: true,
        }),
    ],
});
Sentry.configureScope((scope) => {
    const user = AuthService.getUser();
    if(user !== null) {
        scope.setUser({
            "id": user.id,
            "email": user.email,
            "name": user.name
        });
    }
});*/
